'use client';

import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { CTAButton } from '@/shadcn/ui/button';
import { GROQButton } from '@/sanity/lib/definitions';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/useIsMobile';

interface IStickyBottomButton {
  data?: GROQButton;
  textPreTitle?: {
    firstText?: string;
    secondText?: string;
    secondTextBold?: string;
  };
  className?: string;
}

/**
 * Bottom sticky CTA, meant to be dropped inside the sections that don't have
 * their own CTA (where the persistent CTA isn't redundant). It tracks its host
 * wrapper: it slides up when that section is on screen and slides down once it
 * leaves. Because it only exists in the sections that need it, there's no
 * flickering between CTA-heavy sections — the button simply isn't rendered there.
 */
export default function StickyBottomButton({ data, textPreTitle, className }: IStickyBottomButton) {
  const markerRef = useRef<HTMLSpanElement>(null);
  const [inView, setInView] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    // The marker sits inside the wrapper that groups this sticky with its
    // section, so its parent element represents that section's bounds.
    const host = markerRef.current?.parentElement;
    if (!host) return;

    // How early the button should leave, as a fraction of the viewport height.
    // The button hides once the section's bottom edge has risen to this point
    // from the top of the screen — so a higher value = leaves sooner. Because
    // this is measured against the section's own bottom edge (not just "any
    // part visible"), it behaves the same on short and very tall sections.
    const EXIT_RATIO = 0.85;

    let frame = 0;
    const update = () => {
      frame = 0;
      const viewportH = window.innerHeight;
      const rect = host.getBoundingClientRect();
      // Section has entered from the bottom of the screen.
      const started = rect.top <= viewportH;
      // Section is on its way out: its bottom edge has crossed the exit line.
      const ended = rect.bottom <= viewportH * EXIT_RATIO;
      setInView(started && !ended);
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <>
      <span ref={markerRef} aria-hidden className="hidden" />

      <motion.div
        className={cn("fixed bottom-0 w-full py-[1.5rem] border-t border-white/[12%] bg-black/75 backdrop-blur-[1rem] z-50", className)}
        initial={{ y: '100%' }}
        animate={{ y: inView ? 0 : '100%' }}
        // On mobile, hold the entry (slide up) back a touch; the exit stays immediate.
        transition={{
          duration: 0.5,
          ease: [0.76, 0, 0.24, 1],
          delay: inView && isMobile ? 0.35 : 0,
        }}
      >
        <div className="container flex flex-row gap-[2rem] items-center justify-center w-full mx-auto [&_button]:!min-h-0 [&_button]:!py-[2rem]">
          <div className="flex flex-row items-center gap-[1rem] lg:gap-[2rem] font-nunito text-[#F2F0EF] text-[2rem] font-[200] prose-bold:font-[600] leading-[100%] flex-shrink-0 max-w-[20.5rem] lg:max-w-none">
            {textPreTitle?.firstText && (
              <p className="flex-shrink-0">{textPreTitle.firstText}</p>
            )}
            <span className="w-[4rem] h-[0.125rem] bg-[#898989]" />
            {(textPreTitle?.secondText || textPreTitle?.secondTextBold) && (
              <p>{textPreTitle?.secondText} <b>{textPreTitle?.secondTextBold}</b></p>
            )}
          </div>

          {data && <CTAButton data={data} />}
        </div>
      </motion.div>
    </>
  );
}
